/**
 * Forgot Password Page
 *
 * Password reset request page.
 *
 * @module pages/auth/ForgotPasswordPage
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks';
import { Button, Input, Alert } from '@/components/ui';
import { ArrowLeft, Mail } from 'lucide-react';

// =============================================================================
// Component
// =============================================================================

export function ForgotPasswordPage() {
  usePageTitle('Reset Password');

  const { resetPassword, error, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // Clear any existing auth errors on mount
  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await resetPassword(email);
      setSent(true);
    } catch {
      // Error handled in AuthContext, but we still show success
      // to prevent email enumeration attacks
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex overflow-auto no-scrollbar">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-brand-500 overflow-hidden">
              <svg className="w-10 h-10" viewBox="0 0 32 32" fill="none">
                <path d="M7 10L13 16L7 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                <path d="M13 10L19 16L13 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.75"/>
                <path d="M19 10L25 16L19 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-display font-semibold text-xl text-gray-900">
              Hummingbird
            </span>
          </div>

          {sent ? (
            // Success state
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-success-50 mb-4">
                <Mail className="h-6 w-6 text-success-600" />
              </div>
              <h1 className="text-2xl font-display font-semibold text-gray-900 mb-2">
                Check your email
              </h1>
              <p className="text-gray-500 mb-6">
                We sent a password reset link to{' '}
                <span className="font-medium text-gray-900">{email}</span>.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => setSent(false)}
                  className="text-brand-600 hover:text-brand-700"
                >
                  try again
                </button>.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          ) : (
            // Form state
            <>
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-2xl font-display font-semibold text-gray-900">
                  Reset your password
                </h1>
                <p className="text-gray-500 mt-1">
                  Enter your email, and we'll send you a reset link.
                </p>
              </div>

              {/* Error Alert */}
              {error && (
                <Alert variant="error" className="mb-6" onDismiss={clearError}>
                  {error}
                </Alert>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  required
                />

                <Button type="submit" fullWidth loading={loading}>
                  Send reset link
                </Button>
              </form>

              {/* Back to login */}
              <div className="text-center mt-6">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-brand-500 to-brand-600 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative flex flex-col justify-center px-12 text-white">
          <h2 className="text-4xl font-display font-semibold mb-4">
            Engineered to an unreasonable standard.
          </h2>
          <p className="text-xl text-white/80 max-w-md">
            We spent years shaving off milliseconds that other platforms ignore. You get the benefit.
          </p>

          {/* Stats */}
          <div className="flex gap-12 mt-12">
            <div>
              <p className="text-4xl font-semibold">&lt;50ms</p>
              <p className="text-white/60">Median latency</p>
            </div>
            <div>
              <p className="text-4xl font-semibold">99.99%</p>
              <p className="text-white/60">Uptime SLA</p>
            </div>
            <div>
              <p className="text-4xl font-semibold">300+</p>
              <p className="text-white/60">Edge locations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
